import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@vaiu/ui/card';
import { Checkbox } from '@vaiu/ui/checkbox';
import { Button } from '@vaiu/ui/button';
import { Alert, AlertDescription } from '@vaiu/ui/alert';
import { Separator } from '@vaiu/ui/separator';
import { Badge } from '@vaiu/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { platformApi } from '@/services/platformApi';
import { useUserModel } from '@/hooks/useUserModel';
import { 
  NotificationPreferences, 
  NotificationEventType,
  NOTIFICATION_EVENTS,
  NOTIFICATION_EVENT_CATEGORIES 
} from '@/types/notification';
import { 
  Bell, 
  Mail, 
  MessageSquare, 
  Smartphone,
  Info,
  Loader2,
  Save,
  RotateCcw
} from 'lucide-react';

const NotificationEventPreferences: React.FC = () => {
  const { toast } = useToast();
  const { isMerchant } = useUserModel();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState<NotificationPreferences>(() => {
    const defaultPrefs: NotificationPreferences = {
      enabled: true,
      emailEnabled: true,
      smsEnabled: true,  // Default to true for new users (opt-out model)
      pushEnabled: true,
      events: {}
    };
    
    // Initialize all event preferences with defaults - opt-out model (all enabled)
    NOTIFICATION_EVENTS.forEach(event => {
      defaultPrefs.events[event.type] = {
        email: true,  // All email notifications enabled by default
        sms: event.defaultChannels.includes('sms'), // SMS based on event type defaults
        push: true   // All push notifications enabled by default
      };
    });
    
    return defaultPrefs;
  });
  const [originalPreferences, setOriginalPreferences] = useState<NotificationPreferences | null>(null);

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      setLoading(true);
      // Load from API using the correct endpoint
      // API will return defaults with all notifications enabled if user has no preferences
      const prefs = await platformApi.getPreferences('notification');
      
      // Ensure events object exists and has all event types
      if (!prefs.events) {
        prefs.events = {};
      }
      
      // Initialize any missing event preferences with defaults (opt-out: all enabled)
      // This ensures new events are enabled by default
      NOTIFICATION_EVENTS.forEach(event => {
        if (!prefs.events[event.type]) {
          // Default to true for all channels (opt-out model)
          // Users can turn off notifications they don't want
          prefs.events[event.type] = {
            email: true,  // Default to enabled
            sms: event.defaultChannels.includes('sms'), // SMS defaults based on event type
            push: true    // Default to enabled
          };
        } else {
          // Ensure all channels exist, defaulting missing ones to true
          prefs.events[event.type] = {
            email: prefs.events[event.type]?.email ?? true,
            sms: prefs.events[event.type]?.sms ?? event.defaultChannels.includes('sms'),
            push: prefs.events[event.type]?.push ?? true
          };
        }
      });
      
      // Ensure global channel settings default to true if not set
      if (prefs.emailEnabled === undefined) prefs.emailEnabled = true;
      if (prefs.smsEnabled === undefined) prefs.smsEnabled = true;
      if (prefs.pushEnabled === undefined) prefs.pushEnabled = true;
      if (prefs.enabled === undefined) prefs.enabled = true;
      
      setPreferences(prefs);
      setOriginalPreferences(JSON.parse(JSON.stringify(prefs))); // Deep clone
    } catch (error) {
      console.error('Failed to load notification preferences:', error);
      // Initialize with defaults - all notifications enabled (opt-out model)
      const defaultPrefs: NotificationPreferences = {
        enabled: true,
        emailEnabled: true,
        smsEnabled: true,
        pushEnabled: true,
        events: {}
      };
      
      // Set default preferences for each event - all enabled by default
      NOTIFICATION_EVENTS.forEach(event => {
        defaultPrefs.events[event.type] = {
          email: true,  // All email notifications enabled by default
          sms: event.defaultChannels.includes('sms'), // SMS based on event defaults
          push: true   // All push notifications enabled by default
        };
      });
      
      setPreferences(defaultPrefs);
      setOriginalPreferences(JSON.parse(JSON.stringify(defaultPrefs)));
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async () => {
    try {
      setSaving(true);
      // Save to API using the correct endpoint
      await platformApi.updatePreferences('notification', preferences);
      setOriginalPreferences(JSON.parse(JSON.stringify(preferences)));
      toast({
        title: 'Preferences Saved',
        description: 'Your notification preferences have been updated.',
      });
    } catch (error) {
      console.error('Failed to save notification preferences:', error);
      toast({
        title: 'Save Failed',
        description: 'Unable to save your notification preferences. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = () => {
    if (originalPreferences) {
      setPreferences(JSON.parse(JSON.stringify(originalPreferences)));
    }
  };

  const toggleEventChannel = (
    eventType: NotificationEventType, 
    channel: 'email' | 'sms' | 'push'
  ) => {
    setPreferences(prev => ({
      ...prev,
      events: {
        ...prev.events,
        [eventType]: {
          ...(prev.events[eventType] || { email: false, sms: false, push: false }),
          [channel]: !(prev.events[eventType]?.[channel] ?? false)
        }
      }
    }));
  };

  const toggleGlobalChannel = (channel: 'emailEnabled' | 'smsEnabled' | 'pushEnabled') => {
    setPreferences(prev => ({
      ...prev,
      [channel]: !prev[channel]
    }));
  };

  const hasChanges = JSON.stringify(preferences) !== JSON.stringify(originalPreferences);

  const getChannelIcon = (channel: 'email' | 'sms' | 'push') => {
    switch (channel) {
      case 'email':
        return <Mail className="w-4 h-4" />;
      case 'sms':
        return <MessageSquare className="w-4 h-4" />;
      case 'push':
        return <Smartphone className="w-4 h-4" />;
    }
  };

  const getChannelLabel = (channel: 'email' | 'sms' | 'push') => {
    switch (channel) {
      case 'email':
        return 'Email';
      case 'sms':
        return 'SMS';
      case 'push':
        return 'Push';
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          Loading notification preferences...
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <Bell className="w-5 h-5 mr-2" />
          Notification Preferences
        </CardTitle>
        <CardDescription>
          Notifications are enabled by default. Uncheck to opt out of specific notifications.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Global Channel Toggles */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium">Global Settings</h3>
          <p className="text-sm text-muted-foreground">
            Control notification channels globally. Uncheck to disable a channel for all events.
          </p>
          <div className="flex flex-wrap gap-4">
            <label className="flex items-center space-x-2 cursor-pointer">
              <Checkbox
                checked={preferences.emailEnabled}
                onCheckedChange={() => toggleGlobalChannel('emailEnabled')}
              />
              <span className="flex items-center">
                <Mail className="w-4 h-4 mr-1" />
                Receive Email Notifications
              </span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <Checkbox
                checked={preferences.smsEnabled}
                onCheckedChange={() => toggleGlobalChannel('smsEnabled')}
              />
              <span className="flex items-center">
                <MessageSquare className="w-4 h-4 mr-1" />
                Receive SMS Notifications
              </span>
            </label>
            <label className="flex items-center space-x-2 cursor-pointer">
              <Checkbox
                checked={preferences.pushEnabled}
                onCheckedChange={() => toggleGlobalChannel('pushEnabled')}
              />
              <span className="flex items-center">
                <Smartphone className="w-4 h-4 mr-1" />
                Receive Push Notifications
              </span>
            </label>
          </div>
          
          {!preferences.emailEnabled && !preferences.smsEnabled && !preferences.pushEnabled && (
            <Alert>
              <Info className="w-4 h-4" />
              <AlertDescription>
                All notification channels are disabled. You won't receive any notifications.
              </AlertDescription>
            </Alert>
          )}
        </div>

        <Separator />

        {/* Event-specific Preferences */}
        <div className="space-y-6">
          <div>
            <h3 className="text-sm font-medium">Event Notifications</h3>
            <p className="text-xs text-muted-foreground mt-1">
              All events send notifications by default. Uncheck to opt out of specific event notifications.
            </p>
          </div>
          
          {Object.entries(NOTIFICATION_EVENT_CATEGORIES)
            .filter(([categoryKey]) => {
              // Only show categories that have at least one visible event
              return NOTIFICATION_EVENTS.some(event => {
                if (event.category !== categoryKey) return false;
                if (event.merchantOnly && !isMerchant) return false;
                return true;
              });
            })
            .map(([categoryKey, categoryLabel]) => (
            <div key={categoryKey} className="space-y-3">
              <h4 className="text-sm font-medium text-muted-foreground">{categoryLabel}</h4>
              
              <div className="space-y-2">
                {NOTIFICATION_EVENTS
                  .filter(event => {
                    // Filter by category
                    if (event.category !== categoryKey) return false;
                    // Filter out merchant-only events for non-merchants
                    if (event.merchantOnly && !isMerchant) return false;
                    return true;
                  })
                  .map(event => {
                    // Opt-out model: missing preferences = enabled (true)
                    // Only store preferences when user opts out (unchecks)
                    const eventPrefs = preferences.events?.[event.type] || {
                      email: true,  // Default: enabled (will send notification)
                      sms: event.defaultChannels.includes('sms'), // Based on event defaults
                      push: true    // Default: enabled (will send notification)
                    };
                    
                    return (
                      <div 
                        key={event.type}
                        className="flex flex-col sm:flex-row sm:items-center justify-between p-3 border rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex-1 mb-2 sm:mb-0">
                          <p className="font-medium text-sm">{event.label}</p>
                          <p className="text-xs text-muted-foreground">{event.description}</p>
                        </div>
                        
                        <div className="flex items-center space-x-4">
                          {/* Email */}
                          <label className="flex items-center space-x-1 cursor-pointer">
                            <Checkbox
                              checked={eventPrefs.email && preferences.emailEnabled}
                              disabled={!preferences.emailEnabled}
                              onCheckedChange={() => toggleEventChannel(event.type, 'email')}
                            />
                            <span className="sr-only">Email</span>
                            <Mail className={`w-4 h-4 ${!preferences.emailEnabled ? 'text-gray-300' : ''}`} />
                          </label>
                          
                          {/* SMS */}
                          <label className="flex items-center space-x-1 cursor-pointer">
                            <Checkbox
                              checked={eventPrefs.sms && preferences.smsEnabled}
                              disabled={!preferences.smsEnabled}
                              onCheckedChange={() => toggleEventChannel(event.type, 'sms')}
                            />
                            <span className="sr-only">SMS</span>
                            <MessageSquare className={`w-4 h-4 ${!preferences.smsEnabled ? 'text-gray-300' : ''}`} />
                          </label>
                          
                          {/* Push */}
                          <label className="flex items-center space-x-1 cursor-pointer">
                            <Checkbox
                              checked={eventPrefs.push && preferences.pushEnabled}
                              disabled={!preferences.pushEnabled}
                              onCheckedChange={() => toggleEventChannel(event.type, 'push')}
                            />
                            <span className="sr-only">Push</span>
                            <Smartphone className={`w-4 h-4 ${!preferences.pushEnabled ? 'text-gray-300' : ''}`} />
                          </label>
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          ))}
        </div>

        {/* Legend */}
        <div className="flex items-center justify-center space-x-4 text-xs text-muted-foreground pt-4">
          <span className="flex items-center">
            <Mail className="w-3 h-3 mr-1" /> Email
          </span>
          <span className="flex items-center">
            <MessageSquare className="w-3 h-3 mr-1" /> SMS
          </span>
          <span className="flex items-center">
            <Smartphone className="w-3 h-3 mr-1" /> Push
          </span>
        </div>

        {/* Save/Reset Buttons */}
        <div className="flex justify-end space-x-2 pt-4 border-t">
          {hasChanges && (
            <Button
              variant="outline"
              onClick={resetToDefaults}
              disabled={saving}
            >
              <RotateCcw className="w-4 h-4 mr-2" />
              Reset
            </Button>
          )}
          <Button
            onClick={savePreferences}
            disabled={saving || !hasChanges}
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Preferences
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default NotificationEventPreferences;
